import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';

// The kiosk runs on hardware this app never sees: a 4K/8K lobby TV metres away from the viewer, a
// tablet by the door, a phone someone opened the share link on, and - at the small end - a
// watch-sized display. Every size below therefore scales with `vmin` (the SMALLER viewport side)
// rather than `vw`:
//
//   - `vw` grows with width alone, so on a 3840x2160 (or 21:9) screen a `vw`-sized dial and its
//     stat row grow past the available height and get clipped, and on a tall narrow phone the
//     text collapses even though there is vertical room.
//   - `vmin` is bounded by both sides, so nothing can outgrow the shorter one. Landscape TV sizes
//     follow the height (which is what viewing distance actually tracks), portrait phones and
//     square watch faces follow the width.
//
// Every value is a clamp(floor, vmin, ceiling): the floor keeps a ~200px watch legible, the
// ceiling stops an 8K wall from rendering absurd 900px glyphs. The ratios between elements are
// identical at every size, so the layout is the same design throughout, not a set of breakpoints.
//
// The floors themselves shrink below ~200px (see `floor`), so a 128x128 badge-sized display still
// fits the header, the content and the tab bar on screen instead of clipping them.
// A clamp() floor that holds at `px` on any screen whose shorter side is roughly 200-300px or more,
// and scales down with the viewport below that - `vmin` is picked per value so that a 128x128
// display lands on a size that still fits. `atLeast` stops it shrinking past a legible minimum.
// Phones, tablets and TVs never reach the vmin branch.
const floor = (px: number, vmin: number, atLeast?: number): string =>
  (atLeast === undefined ? `min(${px}px, ${vmin}vmin)` : `max(${atLeast}px, min(${px}px, ${vmin}vmin))`);

export const screen: CSSProperties = {
  width: '100vw',
  height: '100vh',
  background: '#0f2038',
  color: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontFamily: "'Poppins', sans-serif",
  padding: `clamp(${floor(8, 3.2)}, 3vmin, 72px)`,
  boxSizing: 'border-box',
  overflow: 'hidden',
};

// The scrollable middle region between the office/view label and the (always-visible,
// never-overlapping) touch-select/auto-rotate indicator bar. min-height:0 is required for a
// flex child to be allowed to shrink and scroll instead of pushing its flex parent taller.
export const content: CSSProperties = {
  flex: 1,
  minHeight: 0,
  width: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: `clamp(${floor(4, 1.6)}, 1.5vmin, 40px)`,
  boxSizing: 'border-box',
};

export const pairingCode: CSSProperties = {
  fontSize: `clamp(${floor(28, 11)}, 13vmin, 320px)`,
  fontWeight: 700,
  letterSpacing: `clamp(${floor(2, 0.8)}, 1.6vmin, 40px)`,
  fontFamily: 'monospace',
  margin: `clamp(${floor(10, 2.3)}, 3vmin, 64px) 0`,
  textAlign: 'center',
  lineHeight: 1.1,
  // The code is the one string that must never wrap or clip: it is what the admin types into the
  // portal. On a watch-width screen the letter-spacing above would otherwise push it off-screen.
  maxWidth: '100%',
  overflowWrap: 'anywhere',
};

export const pairingLabel: CSSProperties = {
  fontSize: `clamp(${floor(11, 5.5)}, 2.6vmin, 56px)`,
  color: '#9fb2cc',
  textAlign: 'center',
  maxWidth: 'min(1600px, 92%)',
  lineHeight: 1.35,
};

export const viewLabel: NestedCSSProperties = {
  flexShrink: 0,
  fontSize: `clamp(${floor(20, 6.25)}, 5vmin, 140px)`,
  textTransform: 'uppercase',
  letterSpacing: `clamp(${floor(1, 0.4)}, 0.25vmin, 6px)`,
  color: '#f2a621',
  fontWeight: 700,
  marginBottom: `clamp(${floor(6, 1.6)}, 1.5vmin, 32px)`,
  // 10vmin from ~300px up; tapers to nothing on a 128px display, where that space is needed for content.
  marginTop: 'clamp(0px, 20vmin - 30px, 10vmin)',
  textAlign: 'center',
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  $nest: {
    // The one breakpoint: on a badge-sized screen a second header line (e.g. "office · display name")
    // costs room the view itself needs, so keep it to one line and let the end trail off instead.
    '@media (max-width: 200px), (max-height: 200px)': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
};

// Wrapper that gives the dial its width; the SVG inside is `responsive` and fills it, keeping its
// own aspect ratio. Sized off vmin so the dial plus the stat row below always fit the viewport
// height, and capped so an 8K wall does not render a single 3000px dial.
export const gaugeWrap: CSSProperties = {
  width: `clamp(${floor(140, 50)}, 56vmin, 1400px)`,
  maxWidth: '100%',
  flexShrink: 0,
};

export const bigNumber: CSSProperties = {
  fontSize: `clamp(${floor(34, 17)}, 15vmin, 520px)`,
  fontWeight: 700,
  margin: `clamp(${floor(4, 0.8)}, 1vmin, 24px) 0`,
  textAlign: 'center',
  lineHeight: 1.05,
};

export const lunchStack: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: `clamp(${floor(12, 3.9)}, 6vmin, 200px)`,
};

export const lunchIcon: CSSProperties = {
  height: `clamp(${floor(48, 19)}, 22vmin, 720px)`,
  width: 'auto',
};

export const statRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: `clamp(${floor(10, 3.1)}, 5vmin, 120px)`,
  marginTop: `clamp(${floor(8, 1.6)}, 3vmin, 72px)`,
  maxWidth: '100%',
};

export const stat: CSSProperties = {
  textAlign: 'center',
};

export const statValue: CSSProperties = {
  fontSize: `clamp(${floor(18, 6, 10)}, 6vmin, 200px)`,
  fontWeight: 700,
  lineHeight: 1.1,
};

export const statLabel: CSSProperties = {
  fontSize: `clamp(${floor(9, 4.7)}, 4vmin, 44px)`,
  color: '#b9c6d8',
  marginTop: `clamp(${floor(2, 0.8)}, 0.6vmin, 14px)`,
};

export const touchTabs: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  gap: `clamp(${floor(4, 1.6)}, 1.2vmin, 28px)`,
  paddingTop: `clamp(${floor(6, 1.6)}, 2vmin, 48px)`,
  width: '100%',
  boxSizing: 'border-box',
};

export const touchTab = (active: boolean): CSSProperties => ({
  fontSize: `clamp(${floor(10, 3.4, 6)}, 2.1vmin, 46px)`,
  fontWeight: 700,
  padding: `clamp(${floor(5, 1.6)}, 1.4vmin, 32px) clamp(${floor(8, 2.3)}, 2.2vmin, 52px)`,
  borderRadius: `clamp(${floor(6, 2.3)}, 1.2vmin, 26px)`,
  background: active ? '#f2a621' : '#22385a',
  color: active ? '#0f2038' : '#b9c6d8',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export const rotateDots: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  gap: `clamp(${floor(4, 1.6)}, 1vmin, 22px)`,
  paddingTop: `clamp(${floor(6, 1.6)}, 2vmin, 48px)`,
  width: '100%',
  boxSizing: 'border-box',
};

export const rotateDot = (active: boolean): CSSProperties => ({
  width: `clamp(${floor(12, 6.3)}, 2.6vmin, 64px)`,
  height: `clamp(${floor(3, 1.6)}, 0.6vmin, 14px)`,
  borderRadius: `clamp(${floor(2, 0.8)}, 0.4vmin, 8px)`,
  background: active ? '#f2a621' : '#3a4c66',
});

export const errorMessage: CSSProperties = {
  fontSize: `clamp(${floor(12, 6.3)}, 2.8vmin, 64px)`,
  color: '#dd4b39',
  textAlign: 'center',
  maxWidth: 'min(1600px, 92%)',
};

export const tableWrap: CSSProperties = {
  width: '100%',
  // A fixed 900px cap left a 4K wall mostly empty; the room list now uses the space it has, while
  // still stopping short of edge-to-edge text that is hard to scan across a 3-metre screen.
  maxWidth: 'min(2400px, 96%)',
  minWidth: 0,
  overflowX: 'auto',
  boxSizing: 'border-box',
};

export const table: CSSProperties = {
  width: '100%',
  // Not a fixed 340px: on a watch-width screen that forced a horizontal scroll on what is a
  // three-column, mostly-numeric table. It compresses to the viewport instead and only scrolls
  // when the room names genuinely cannot fit.
  minWidth: 'min(320px, 100%)',
  borderCollapse: 'collapse',
  fontSize: `clamp(${floor(10, 5.5)}, 2.6vmin, 60px)`,
};

export const th = (align: 'left' | 'center' = 'left'): CSSProperties => ({
  textAlign: align,
  padding: `clamp(${floor(3, 0.8)}, 1vmin, 24px) clamp(${floor(4, 1.6)}, 1.4vmin, 32px)`,
  color: '#9fb2cc',
  fontSize: `clamp(${floor(8, 4.7)}, 1.9vmin, 42px)`,
  textTransform: 'uppercase',
  letterSpacing: `clamp(${floor(0.5, 0.25)}, 0.12vmin, 3px)`,
  borderBottom: '1px solid #22385a',
  whiteSpace: 'nowrap',
});

export const td = (align: 'left' | 'center' = 'left'): CSSProperties => ({
  textAlign: align,
  padding: `clamp(${floor(3, 0.8)}, 1vmin, 24px) clamp(${floor(4, 1.6)}, 1.4vmin, 32px)`,
  borderBottom: '1px solid #1a2c47',
  wordBreak: 'break-word',
});
